import React, { useState, useEffect } from 'react';
import { X, Heart, MapPin, Calendar, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
// import { base44 } from '@/api/base44Client';

const FLYER_IMG = "https://media.base44.com/images/public/69e9924d4f40ee89768ca40a/1fa67680f_ChatGPTImageMay25202609_40_47AM.png";

export default function MedicalMissionPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('intro'); // 'intro' | 'form' | 'success'
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('missionPopupDismissed_');
    if (!dismissed) {
      const timer = setTimeout(() => setIsOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setIsOpen(false);
  };

  const handleDismiss2 = () => {
    sessionStorage.setItem('missionPopupDismissed', 'true');
    setIsOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
    //   await base44.integrations.Core.SendEmail({
    //     to: 'info@roseofjericho-cd.org',
    //     subject: '2027 Ghana Medical Mission — Interest List Sign-Up',
    //     body: `New interest list sign-up:\n\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || 'Not provided'}\nRole/Background: ${form.role || 'Not provided'}`
    //   });
      setStep('success');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && handleDismiss()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 z-10 bg-black/30 hover:bg-black/50 text-white rounded-full p-1.5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Hero image section */}
            <div className="relative h-56 sm:h-72 overflow-hidden">
              <img
                src={FLYER_IMG}
                alt="Ghana Medical Mission 2027"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="w-4 h-4 text-red-400 fill-red-400" />
                  <span className="text-xs font-medium tracking-widest uppercase text-red-300">Upcoming Mission</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold leading-tight">
                  Ghana Medical Mission Tour
                </h2>
                <p className="text-yellow-300 font-semibold text-lg italic mt-0.5">
                  Healing. Serving. Changing Lives.
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {step === 'intro' && (
                <>
                  {/* Key details */}
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className=" border border-rose-300 rounded-xl p-3 text-center">
                      <Calendar className="w-5 h-5 text-secondary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">June 8–18</p>
                      <p className="text-xs text-muted-foreground">2027</p>
                    </div>
                    <div className="border border-rose-300  rounded-xl p-3 text-center">
                      <MapPin className="w-5 h-5 text-secondary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Accra</p>
                      <p className="text-xs text-muted-foreground">Ghana</p>
                    </div>
                    <div className="border border-rose-300  rounded-xl p-3 text-center">
                      <Users className="w-5 h-5 text-secondary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">11-Day</p>
                      <p className="text-xs text-muted-foreground">Mission</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground text-sm leading-relaxed mb-5">
                    Join us for a life-changing 11-day medical mission to Ghana hosted by <strong className="text-foreground">Rose of Jericho Community Development</strong>. Provide care, share hope, build lasting connections, and be the hands and feet of love.
                  </p>

                  <div className="bg-muted/10 rounded-xl p-4 mb-5 text-sm">
                    <p className="font-semibold text-foreground mb-2">What's Included:</p>
                    <ul className="text-muted-foreground space-y-1 text-xs">
                      <li> Accommodations at AnoMansa Beach Resort & Best Western Plus</li>
                      <li> Delicious, authentic Ghanaian cuisine</li>
                      <li> Cultural & historical tour sites</li>
                      <li> Local market shopping experiences</li>
                    </ul>
                  </div>

                  <p className="text-xs text-center text-muted-foreground mb-4 font-medium">
                    ⚠️ Spaces are limited — join our interest list today!
                  </p>

                  <Button
                    // onClick={() => setStep('form')}
                    onClick={() => window.open("https://heartsformissions.org/register")}
                    className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-3 text-base font-semibold"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Join the Interest List
                  </Button>
                  <button
                    onClick={handleDismiss2}
                    className="w-full text-center text-xs text-muted-foreground mt-3 hover:text-foreground transition-colors"
                  >
                    Not Interested
                  </button>
                </>
              )}

              {step === 'form' && (
                <>
                  <h3 className="font-heading font-bold text-xl text-foreground mb-1">Sign Up for Updates</h3>
                  <p className="text-muted-foreground text-sm mb-5">
                    Add your name to our interest list and we'll keep you informed as details are finalized.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <Input
                      placeholder="Full Name *"
                      required
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                    />
                    <Input
                      type="email"
                      placeholder="Email Address *"
                      required
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                    />
                    <Input
                      type="tel"
                      placeholder="Phone Number (optional)"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                    />
                    <Input
                      placeholder="Your Role / Background (e.g. Nurse, Doctor, Volunteer)"
                      value={form.role}
                      onChange={e => setForm({ ...form, role: e.target.value })}
                    />
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-3 font-semibold"
                    >
                      {loading ? 'Submitting...' : 'Submit My Interest'}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setStep('intro')}
                      className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      ← Back
                    </button>
                  </form>
                </>
              )}

              {step === 'success' && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-green-600 fill-green-600" />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-foreground mb-2">Thank You!</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                    You're on the list! We'll reach out with more details about the <strong>2027 Ghana Medical Mission Tour</strong> as they become available.
                  </p>
                  <p className="text-xs text-muted-foreground italic mb-6">
                    "Serve. Love. Impact. Together, we can make a difference."
                  </p>
                  <Button
                    onClick={handleDismiss2}
                    className="bg-primary hover:bg-primary/90 text-white rounded-full px-8"
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}